import React from 'react';
import {t} from '@lingui/core/macro';

import HintQuestion from 'shared/ui/Hint/HintQuestion';
import RadioGroup from 'shared/ui/RadioGroup/RadioGroup';
import {TagsChipField, CheckInterval, GeoCheck, EditableFieldRow, MonitorBatchOperationsForm} from 'entities/monitor';
import {NotifyChannels} from 'entities/notification';
import TextArea from 'shared/ui/TextArea/TextArea';
import TimeoutSlider from 'shared/ui/Slider/TimeoutSlider';
import {IMassOperationAction} from 'shared/ui/MassOperations/MassOperations';
import {parsePortTargets} from 'features/port/lib/parsePortTargets';
import {PORT_BATCH_EDIT_FIELDS, PORT_BATCH_EDIT_FIELD_BY_KEY} from 'features/port/lib/portBatchEditFields';
import {PORT_DEFAULT_TIMEOUT} from 'features/port/lib/portMonitorFormHelper';
import {usePortMonitorBatchOperationsForm} from 'features/port/model/usePortMonitorBatchOperationsForm';

import styles from './PortMonitorForm.module.scss';

interface IPortMonitorBatchOperationsFormProps {
    handleClose: () => void,
    action?: IMassOperationAction,
    onSuccess?: () => void,
}

const PortMonitorBatchOperationsForm: React.FC<IPortMonitorBatchOperationsFormProps> = ({
    handleClose,
    action,
    onSuccess,
}) => {
    const state = usePortMonitorBatchOperationsForm({actionValue: action?.value, handleClose, onSuccess});
    const {form, removeField} = state;
    const {register, formState, control, setValue, watch} = form;
    const {errors} = formState;

    const renderEditField = (key: string) => {
        switch (key) {
            case 'desired_state':
                return (
                    <EditableFieldRow key={key} onRemove={() => removeField(key)}>
                        <div className={styles.block}>
                            <div className={styles.label}>
                                {t`Желаемое состояние`}
                                <HintQuestion placement="top-start" className={styles.icon}>
                                    {t`Инцидент откроется, когда порт окажется не в этом состоянии.`}
                                </HintQuestion>
                            </div>
                            <RadioGroup
                                name="desired_state"
                                control={control}
                                options={[
                                    {value: 'open', label: t`Открыт`},
                                    {value: 'closed', label: t`Закрыт`},
                                ]}
                            />
                        </div>
                    </EditableFieldRow>
                );
            case 'check_interval':
                return (
                    <EditableFieldRow key={key} onRemove={() => removeField(key)}>
                        <CheckInterval item={undefined} setValue={setValue} watch={watch} errors={errors}/>
                    </EditableFieldRow>
                );
            case 'timeout':
                return (
                    <EditableFieldRow key={key} onRemove={() => removeField(key)}>
                        <div className={styles.block}>
                            <div className={styles.label}>{t`Таймаут проверки`}</div>
                            <TimeoutSlider
                                name="timeout"
                                setValue={setValue}
                                defaultValue={PORT_DEFAULT_TIMEOUT}
                            />
                        </div>
                    </EditableFieldRow>
                );
            case 'locations':
                return (
                    <EditableFieldRow key={key} onRemove={() => removeField(key)}>
                        <GeoCheck control={control} setValue={setValue} showThreshold={false}/>
                    </EditableFieldRow>
                );
            case 'threshold':
                return (
                    <EditableFieldRow key={key} onRemove={() => removeField(key)}>
                        <GeoCheck control={control} setValue={setValue} showLocations={false}/>
                    </EditableFieldRow>
                );
            case 'tags':
                return (
                    <EditableFieldRow key={key} onRemove={() => removeField(key)}>
                        <TagsChipField control={control} label={t`Теги`} error={errors?.tags?.message}/>
                    </EditableFieldRow>
                );
            default:
                return (
                    <EditableFieldRow
                        key={key}
                        label={PORT_BATCH_EDIT_FIELD_BY_KEY.notify.label()}
                        onRemove={() => removeField(key)}
                    >
                        <NotifyChannels
                            control={control}
                            register={register}
                            emailFieldName="notify_by_email"
                            additionalEmailsFieldName="additional_emails"
                            telegramFieldName="telegram_chats"
                            maxFieldName="max_chats"
                        />
                    </EditableFieldRow>
                );
        }
    };

    return (
        <MonitorBatchOperationsForm
            action={action}
            state={state}
            edit={{fields: PORT_BATCH_EDIT_FIELDS, renderField: renderEditField}}
            selectedLabel={t`Выбрано мониторов портов`}
            removeByList={{
                label: t`Адреса`,
                hint: t`Удалятся все мониторы, адреса которых есть в списке.`,
                error: errors?.host_port?.message,
                field: (
                    <TextArea
                        rows={5}
                        register={register}
                        placeholder={t`Вводите каждый адрес с новой строки в формате ip:port`}
                        name="host_port"
                        constraints={{
                            validate: (value: string) =>
                                parsePortTargets(value).length > 0 || t`Укажите хотя бы один адрес`,
                        }}
                    />
                ),
            }}
        />
    );
};

export default PortMonitorBatchOperationsForm;
